import { TokenReissueResponse } from '@/apis/handler/auth/authSchema';
import axios from 'axios';
import { PROTECTED_PATH } from 'shared/constants';
import { getPathWithoutLang } from 'shared/lib/converter';
import { INFO_STORE_KEY, useInfoStore } from 'store/useInfoStore';

export const request = axios.create({
  baseURL: process.env.REACT_APP_API,
  timeout: 20000,
  headers: {
    Accept: 'application/json',
  },
  withCredentials: true,
});

// Helper function to detect language from URL path (for development)
const getLanguageFromPath = (): string => {
  const pathname = window.location.pathname;
  const languageCode = pathname.split('/')[1];

  switch (languageCode) {
    case 'zh':
      return 'zh-CN';
    case 'vi':
      return 'vi';
    case 'ja':
      return 'ja';
    default:
      return 'en-US';
  }
};

request.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) {
      const { accessToken: latestToken } = useInfoStore.getState();
      if (latestToken) {
        config.headers.Authorization = `Bearer ${latestToken}`;
      }
    }

    // Add Accept-Language header
    const acceptLanguage = getLanguageFromPath();
    config.headers['Accept-Language'] = acceptLanguage;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const REISSUE_LOCK_NAME = 'checkmate-token-reissue';

// 이 탭에서 진행 중인 재발급 — 동시에 거절된 요청들이 같은 결과를 기다린다
let reissuePromise: Promise<string> | null = null;

/** 다른 탭이 재발급하면 메모리 스토어는 그대로라 공유 저장소에서 직접 읽는다 */
const getSharedAccessToken = (): string => {
  try {
    return JSON.parse(localStorage.getItem(INFO_STORE_KEY) ?? 'null')?.state?.accessToken ?? '';
  } catch {
    return '';
  }
};

/** JWT 의 만료 시각(exp)이 아직 남았는지 — 다른 탭이 저장한 토큰도 그새 만료됐을 수 있다 */
const isTokenUnexpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

const saveAccessToken = (accessToken: string) => {
  useInfoStore.getState().setInfo({ ...useInfoStore.getState().getInfo(), accessToken });
};

const reissueAccessToken = async (expiredToken: string): Promise<string> => {
  // 다른 요청·탭이 이미 새 토큰을 받아뒀으면 재발급권을 또 쓰지 않는다 (서버는 재발급권을 1회용으로 교체함)
  const sharedToken = getSharedAccessToken();
  if (sharedToken && sharedToken !== expiredToken && isTokenUnexpired(sharedToken)) {
    saveAccessToken(sharedToken);
    return sharedToken;
  }

  const { data } = await axios.post<TokenReissueResponse>(
    `${process.env.REACT_APP_API}/auth/token/reissue/member`,
    {},
    { withCredentials: true },
  );

  // NOTE: http code is returned as 200 even if the request is failed. so we need to check isSuccess
  // NOTE: refresh 토큰이 만료되면 로그아웃 처리한다
  if (!data.isSuccess) {
    useInfoStore.getState().resetInfo?.();
    // NOTE: user should be logged out even when token is expired (쿠키 정리 — 실패해도 무시)
    await axios.post(`${process.env.REACT_APP_API}/auth/logout`).catch(() => undefined);
    throw new Error('Token reissue failed');
  }

  saveAccessToken(data.result.accessToken);
  return data.result.accessToken;
};

/** 탭 안(공유 Promise)·탭 사이(Web Locks) 모두에서 재발급이 한 번에 하나만 돌게 한다 */
const reissueAccessTokenOnce = (expiredToken: string): Promise<string> => {
  if (!reissuePromise) {
    const run = () => reissueAccessToken(expiredToken);
    const locked = navigator.locks ? navigator.locks.request(REISSUE_LOCK_NAME, run) : run();
    reissuePromise = locked.finally(() => {
      reissuePromise = null;
    });
  }
  return reissuePromise;
};

request.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const expiredToken = `${originalRequest.headers.Authorization ?? ''}`.replace('Bearer ', '');
        const newAccessToken = await reissueAccessTokenOnce(expiredToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return axios(originalRequest);
      } catch (refreshError) {
        // NOTE: 재발급에 실패하면 메인 페이지로 이동하도록 합니다
        console.error('refreshError', refreshError);

        // 보호된 경로에서 접근한 경우 redirect 파라미터와 함께 홈으로 이동
        const pathname = window.location.pathname;

        const pathWithoutLang = getPathWithoutLang(pathname);
        const isProtectedPath = PROTECTED_PATH.some((path) => pathWithoutLang.startsWith(path));

        if (isProtectedPath) {
          // 보호된 경로인 경우 redirect 파라미터와 함께 홈으로 이동
          window.location.href = `/?redirect=${encodeURIComponent(pathname)}`;
        } else {
          // NOTE: 500 에러인 경우 에러 바운더리에서 처리하도록 하기 위해 500 에러가 아닌 경우만 '/'로 이동
          // NOTE: 만약 '/'로 이동하면 다시 '/'로 이동하는 재귀에 빠지게 되는 문제 있음
          if (error.response?.status !== 500) {
            window.location.href = '/';
          }
        }
      }
    }
    return Promise.reject(error);
  },
);
